import { computed, watch, type MaybeRefOrGetter, toValue } from "vue";
import { Org } from "tf-api-client";
import api from "../api";
import { useAuthStore } from "../stores/auth.store";
import { createOrgCache } from "./org-cache";

type OrgStatus = {
  status: Org.OrganizationStatus;
  // why it was suspended: the newest status change, when frozen
  frozenReason: Org.OrganizationStatusChangeReason | null;
};

// Status (active/frozen) of each organization, with the suspension reason,
// for sessions whose token is unrestricted: an MSP admin viewing a Managed
// org that was suspended (plan section 4.3.3). Never a global flag.
const cache = createOrgCache<OrgStatus>(async (orgId) => {
  const org = await api.org.getOrg(orgId);
  if (org.status !== Org.OrganizationStatus.Frozen) {
    return { status: org.status, frozenReason: null };
  }

  // newest first: the change that froze it
  const changes = await api.org
    .getOrgStatusChanges(orgId, { size: 1 })
    .catch((err) => {
      console.error("Failed to fetch the suspension reason", err);
      return null;
    });
  const latest = changes?.items[0];
  return {
    status: org.status,
    frozenReason:
      latest?.toStatus === Org.OrganizationStatus.Frozen ? latest.reason : null,
  };
});

export function invalidateOrgStatus(
  orgId?: string,
  options?: { refetch?: boolean },
) {
  cache.invalidate(orgId, options);
}

export function useOrgStatus(
  orgId: MaybeRefOrGetter<string | null | undefined>,
) {
  const auth = useAuthStore();
  const id = computed(() => toValue(orgId) || null);
  // the restricted tokens can't read other organizations: nothing to fetch
  const allowed = computed(() => !auth.suspended && !auth.onboarding);

  cache.retain(id);

  const entry = computed(() => (id.value ? cache.entryFor(id.value) : null));

  // A 403 `organization_frozen` (marked by the session handler) counts at
  // once; the status read afterwards brings the reason. The mark is dropped
  // when the organization is restored or selected again.
  const markedFrozen = computed(
    () => !!id.value && auth.frozenOrgIds.includes(id.value),
  );
  const frozen = computed(
    () =>
      markedFrozen.value ||
      entry.value?.data.value?.status === Org.OrganizationStatus.Frozen,
  );
  const frozenReason = computed(
    () => entry.value?.data.value?.frozenReason ?? null,
  );

  watch(
    [id, allowed],
    ([orgIdValue, isAllowed]) => {
      if (orgIdValue && isAllowed) cache.fetch(orgIdValue);
    },
    { immediate: true },
  );

  return { frozen, frozenReason };
}
